import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Reorder,
  DragIndicator,
} from '@mui/icons-material';

// Default section orders
const defaultLeftSectionOrder = ['education', 'skills', 'softwareSkills', 'languages', 'certifications'];
const defaultRightSectionOrder = ['summary', 'experience', 'volunteering', 'accomplishments', 'references', 'interests', 'websites', 'additionalInfo'];

export const resumeMeta = {
  hasPhoto: true,
  columns: 2,
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const pageSizePxMap = {
  A4:        { width: 794,  height: 1123 },
  Letter:    { width: 816,  height: 1056 },
  Legal:     { width: 816,  height: 1344 },
  A3:        { width: 1123, height: 1587 },
  Executive: { width: 696,  height: 1010 },
};

const Resume50 = ({
  color = "#1a2537",
  font = "Arial, Helvetica, sans-serif",
  fontSize = "14px",
  headingSize = "24px",
  fontStyle = "normal",
  sectionSpacing = 50,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  photo: photoProp = "",
  formData: formDataProp = {},
  workExperiences: workExperiencesProp = [],
  exporting = false,
  enableDragDrop = true,
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;
  const pagePx = pageSizePxMap[pageSize] || pageSizePxMap.A4;

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageBreaks, setPageBreaks] = useState([]);

  // Section ordering states
  const [leftSectionOrder, setLeftSectionOrder]   = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex]         = useState(null);
  const [dragOverSide, setDragOverSide]           = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // State for all data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    city: "",
    country: "",
    pinCode: "",
    phone: "",
    email: "",
    currentPosition: "",
    linkedIn: "",
  });
  const [photo, setPhoto] = useState("");
  const [workHistory, setWorkHistory] = useState([]);
  const [education, setEducation] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [skills, setSkills] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [references, setReferences] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [profileSummary, setProfileSummary] = useState("");
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});
  const [loading, setLoading] = useState(true);

  const rightContentRef = useRef(null);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem("token");
        
        const storedEntries = localStorage.getItem("educationEntries");
        if (storedEntries) {
          setEducationEntries(JSON.parse(storedEntries));
        }
        
        const storedSkills = localStorage.getItem("skills");
        if (storedSkills) {
          setSavedSkills(JSON.parse(storedSkills));
          setSkills(JSON.parse(storedSkills));
        }
        
        const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
        if (storedSummaries.length > 0) {
          const summary = storedSummaries[storedSummaries.length - 1];
          setSavedSummary(summary);
          setProfileSummary(summary);
        }
        
        const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
        setSavedAdditionalInfo(storedAdditionalInfo);
        
        const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
        setSavedLanguages(storedLanguages);
        
        const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
        setSavedAccomplishments(storedAccomplishments);
        
        const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
        setSavedCertifications(storedCertifications);
        
        const storedReferencesLocal = JSON.parse(localStorage.getItem("referencesList")) || [];
        setSavedReferences(storedReferencesLocal);
        
        const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
        setSoftwareSkills(storedSoftwareSkills);
        
        const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
        setSavedVolunteering(storedVolunteering);
        
        const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
        setSavedInterests(storedInterests);
        
        const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
        setSavedWebsites(storedWebsites);
        
        const userId = localStorage.getItem("userId");
        let sectionsData = {};

        if (userId) {
          const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
          
          if (currentCustomSections.length > 0) {
            currentCustomSections.forEach(sectionName => {
              const userCustomKey = `custom_${userId}_${sectionName}`;
              try {
                const customSectionData = localStorage.getItem(userCustomKey);
                if (customSectionData) {
                  const parsedData = JSON.parse(customSectionData);
                  const items = parsedData.items || [];
                  
                  const validItems = items.filter(item => item && item.trim() !== '');
                  
                  if (validItems.length > 0) {
                    sectionsData[sectionName] = validItems;
                  }
                }
              } catch (error) {
                console.error(`Error loading custom section ${sectionName}:`, error);
              }
            });
          }
        }

        setCustomSections(sectionsData);

        const savedLeftOrder = localStorage.getItem("resume50LeftSectionOrder");
        const savedRightOrder = localStorage.getItem("resume50RightSectionOrder");
        const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
        
        if (savedLeftOrder) {
          try {
            const parsedLeft = JSON.parse(savedLeftOrder).filter(s => !s.startsWith('custom_'));
            setLeftSectionOrder(parsedLeft);
          } catch {}
        }
        
        if (savedRightOrder) {
          try {
            const parsedRight = JSON.parse(savedRightOrder);
            const filteredRight = parsedRight.filter(s => !s.startsWith('custom_'));
            setRightSectionOrder([...filteredRight, ...currentCustomIds]);
          } catch {
            setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomIds]);
          }
        } else {
          setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomIds]);
        }

        if (photoProp) {
          setPhoto(photoProp);
        }
        if (formDataProp && Object.keys(formDataProp).length > 0) {
          setFormData(formDataProp);
        }
        if (workExperiencesProp && workExperiencesProp.length > 0) {
          setWorkHistory(workExperiencesProp);
        }

        if (!token) {
          setLoading(false);
          return;
        }

        try {
          const contactRes = await axios.get("https://resume-maker-lc.onrender.com/api/contact/me", {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (contactRes.data) {
            setFormData(prev => ({
              firstName: contactRes.data.firstName || prev.firstName || "",
              lastName: contactRes.data.lastName || prev.lastName || "",
              city: contactRes.data.city || prev.city || "",
              country: contactRes.data.country || prev.country || "",
              pinCode: contactRes.data.pinCode || prev.pinCode || "",
              phone: contactRes.data.phone || prev.phone || "",
              email: contactRes.data.email || prev.email || "",
              currentPosition: contactRes.data.currentPosition || prev.currentPosition || "",
              linkedIn: contactRes.data.linkedIn || prev.linkedIn || "",
            }));

            if (contactRes.data.photo && !photoProp) {
              setPhoto(
                contactRes.data.photo.startsWith("http")
                  ? contactRes.data.photo
                  : `https://resume-maker-lc.onrender.com${contactRes.data.photo}`
              );
            }
          }
        } catch (err) {
          console.log("No contact data found");
        }

        if (workExperiencesProp.length === 0) {
          try {
            const workRes = await axios.get("https://resume-maker-lc.onrender.com/api/work/me", {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (workRes.data && Array.isArray(workRes.data)) {
              setWorkHistory(workRes.data);
            }
          } catch (err) {
            console.log("No work history found");
          }
        }

        try {
          const eduRes = await axios.get("https://resume-maker-lc.onrender.com/api/education/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (eduRes.data && Array.isArray(eduRes.data)) {
            setEducation(eduRes.data);
          }
        } catch (err) {
          console.log("No education found");
        }

        try {
          const refRes = await axios.get("https://resume-maker-lc.onrender.com/api/references/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (refRes.data && Array.isArray(refRes.data)) {
            setReferences(refRes.data);
          }
        } catch (err) {
          console.log("No references found");
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    if (photoProp) setPhoto(photoProp);
  }, [photoProp]);

  useEffect(() => {
    if (formDataProp && Object.keys(formDataProp).length > 0) {
      setFormData(prev => ({
        ...prev,
        firstName:       formDataProp.firstName       !== undefined ? formDataProp.firstName       : prev.firstName,
        lastName:        formDataProp.lastName        !== undefined ? formDataProp.lastName        : prev.lastName,
        city:            formDataProp.city            !== undefined ? formDataProp.city            : prev.city,
        country:         formDataProp.country         !== undefined ? formDataProp.country         : prev.country,
        pinCode:         formDataProp.pinCode         !== undefined ? formDataProp.pinCode         : prev.pinCode,
        phone:           formDataProp.phone           !== undefined ? formDataProp.phone           : prev.phone,
        email:           formDataProp.email           !== undefined ? formDataProp.email           : prev.email,
        currentPosition: formDataProp.currentPosition !== undefined ? formDataProp.currentPosition : prev.currentPosition,
        linkedIn:        formDataProp.linkedIn        !== undefined ? formDataProp.linkedIn        : prev.linkedIn,
      }));
    }
  }, [formDataProp]);

  useEffect(() => {
    if (workExperiencesProp && workExperiencesProp.length > 0) {
      setWorkHistory(workExperiencesProp);
    }
  }, [workExperiencesProp]);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume50LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume50RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [leftSectionOrder, rightSectionOrder]);

  // Drag handlers
  const mkDragStart = (side, order) => (e, idx) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ idx, side, id: order[idx] }));
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragOver  = (e, idx, side) => { e.preventDefault(); setDragOverIndex(idx); setDragOverSide(side); };
  const handleDragLeave = () => { setDragOverIndex(null); setDragOverSide(null); };
  const handleDragEnd   = e  => { e.currentTarget.style.opacity = "1"; setDragOverIndex(null); setDragOverSide(null); };

  const handleDrop = (e, targetIdx, targetSide) => {
    e.preventDefault();
    const { idx: srcIdx, side: srcSide, id } = JSON.parse(e.dataTransfer.getData("text/plain"));
    let lo = [...leftSectionOrder], ro = [...rightSectionOrder];
    if (srcSide === targetSide) {
      const arr = srcSide === "left" ? lo : ro;
      const [m] = arr.splice(srcIdx, 1);
      arr.splice(targetIdx, 0, m);
    } else {
      (srcSide === "left" ? lo : ro).splice(srcIdx, 1);
      (targetSide === "left" ? lo : ro).splice(targetIdx, 0, id);
    }
    setLeftSectionOrder(lo);  localStorage.setItem("resume50LeftSectionOrder",  JSON.stringify(lo));
    setRightSectionOrder(ro); localStorage.setItem("resume50RightSectionOrder", JSON.stringify(ro));
    setDragOverIndex(null); setDragOverSide(null);
    e.currentTarget.style.opacity = "1";
  };

  const resetAll = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
    localStorage.removeItem("resume50LeftSectionOrder");
    localStorage.removeItem("resume50RightSectionOrder");
  };

  const getSectionTitle = (sectionId) => {
    const titles = {
      summary:         'Profile',
      skills:         'Expertise',
      experience:     'Experiences',
      education:      'Education',
      certifications: 'Certifications',
      languages:      'Languages',
      accomplishments:'Rewards',
      additionalInfo: 'Additional Information',
      softwareSkills: 'Software Skills',
      volunteering:   'Volunteering',
      interests:      'Interests',
      websites:       'Websites / Profiles',
      references:     'Reference',
    };
    if (sectionId.startsWith('custom_'))
      return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId.toUpperCase();
  };

  const renderDragPanel = (side, sections) => (
    <Box style={{ flex: 1, minWidth: 250 }}>
      <Typography variant="h6" style={{ marginBottom: "12px", fontWeight: 600 }}>
        {side === "left" ? "Left (Sidebar)" : "Right (Main)"} — {sections.length} sections
      </Typography>
      <Box style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 4, padding: 8 }}>
        {sections.map((id, idx) => (
          <Paper key={id} draggable
            onDragStart={e => mkDragStart(side, sections)(e, idx)}
            onDragOver={e => handleDragOver(e, idx, side)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, idx, side)}
            onDragEnd={handleDragEnd}
            style={{
              padding: "10px 14px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              backgroundColor: dragOverIndex === idx && dragOverSide === side ? "#f0f0f0" : "white",
              border: dragOverIndex === idx && dragOverSide === side
                ? `2px dashed ${color}`
                : "1px solid #ddd",
              cursor: "grab",
            }}>
            <DragIndicator color="action" style={{ marginRight: 8 }} />
            <Typography style={{ flex: 1, fontSize: "13px" }}>{getSectionTitle(id)}</Typography>
            <Typography variant="body2" color="textSecondary">{idx + 1}</Typography>
          </Paper>
        ))}
        {!sections.length && (
          <Typography style={{ padding: 16, textAlign: "center", color: "#888", fontSize: "13px" }}>
            Drag sections here from the other side.
          </Typography>
        )}
      </Box>
      <Button size="small" variant="outlined" style={{ marginTop: 8 }}
        onClick={() => side === "left"
          ? (setLeftSectionOrder(defaultLeftSectionOrder),  localStorage.removeItem("resume50LeftSectionOrder"))
          : (setRightSectionOrder(defaultRightSectionOrder), localStorage.removeItem("resume50RightSectionOrder"))}>
        Reset {side === "left" ? "Left" : "Right"}
      </Button>
    </Box>
  );

  const getEducationEntries = () => {
    const educationEntriesForResume = localStorage.getItem("educationEntriesForResume");
    let entries = [];

    if (educationEntriesForResume) {
      entries = JSON.parse(educationEntriesForResume);
    } else if (educationEntries.length > 0) {
      entries = educationEntries.map(entry => {
        if (entry.educationLevel === "school") {
          return {
            id: entry.id,
            degree: `${entry.standard || ""}${entry.standard ? " Standard" : ""}`,
            fieldOfStudy: entry.stream || "General Education",
            schoolName: entry.schoolName || "",
            schoolLocation: entry.schoolLocation || "",
            gradMonth: "",
            gradYear: "",
            additionalCoursework: [
              entry.district && entry.district,
              entry.percentage && `Percentage: ${entry.percentage}%`,
            ].filter(Boolean).join(" | ")
          };
        } else {
          return {
            id: entry.id,
            degree: `${entry.degreeName || ""}${entry.programType ? ` (${entry.programType})` : ""}`,
            fieldOfStudy: entry.specialization || entry.degreeType || "",
            schoolName: entry.collegeName || "",
            schoolLocation: entry.collegeLocation || "",
            gradMonth: entry.gradMonth || "",
            gradYear: entry.gradYear || "",
            additionalCoursework: [
              entry.cgpa && `CGPA: ${entry.cgpa}`,
              entry.percentage && `Percentage: ${entry.percentage}%`,
              entry.grade && `Grade: ${entry.grade}`,
              entry.additionalCoursework
            ].filter(Boolean).join(" | ")
          };
        }
      });
    } else if (education.length > 0) {
      entries = education;
    }

    return entries;
  };

  const finalEducationEntries = getEducationEntries();
  const fullName = `${formData.firstName || ""} ${formData.lastName || ""}`.trim();
  const firstName = formData.firstName || "Jennifer";
  const lastName = formData.lastName || "Anderson";
  const position = formData.currentPosition || "Art Director";
  const allSkills = savedSkills.length > 0 ? savedSkills : skills;
  const allReferences = references.length > 0 ? references : savedReferences;
  const allRewards = savedAccomplishments.length > 0 ? savedAccomplishments : [];

  const textSafe = {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
  };

  const renderSectionContent = (sectionId, inSidebar = false) => {
    const hStyle = inSidebar ? {
      fontSize: headingSize,
      fontWeight: 'bold',
      marginBottom: `${paragraphSpacing / 2}px`,
      color: '#ffffff',
      textTransform: 'uppercase',
      letterSpacing: `${1.5 * lineWeight}px`,
      ...textSafe,
    } : {
      fontSize: headingSize,
      fontWeight: 'bold',
      marginBottom: `${paragraphSpacing}px`,
      color: color,
      textTransform: 'uppercase',
      letterSpacing: `${1.2 * lineWeight}px`,
      ...textSafe,
    };

    const mainTitleColor = inSidebar ? '#ffffff' : '#333333';
    const secondaryColor = inSidebar ? '#d0d0d0' : '#555555';
    const tertiaryColor = inSidebar ? '#b0b0b0' : '#555555';
    const quaternaryColor = inSidebar ? '#a0a0a0' : '#777777';

    switch (sectionId) {
      case 'education':
        return finalEducationEntries && finalEducationEntries.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h3 style={hStyle}>EDUCATION</h3>
            {finalEducationEntries.map((edu, i) => {
              const startYear = edu.startMonth && edu.startYear 
                ? formatDate(edu.startMonth, edu.startYear)
                : (edu.startYear || "");
              const endYear = edu.currentlyStudying 
                ? "Present" 
                : (edu.gradMonth && edu.gradYear
                  ? formatDate(edu.gradMonth, edu.gradYear)
                  : (edu.endMonth && edu.endYear 
                    ? formatDate(edu.endMonth, edu.endYear)
                    : (edu.gradYear || edu.endYear || "Present")));

              return (
                <div key={edu.id || i} style={{ marginBottom: `${paragraphSpacing}px`, color: secondaryColor, ...textSafe }}>
                  <h4 style={{ fontSize: fontSize, fontWeight: 'bold', margin: '0 0 4px 0', color: mainTitleColor, ...textSafe }}>
                    {edu.degree || edu.qualification}
                  </h4>
                  <p style={{ fontSize: fontSize, color: tertiaryColor, margin: '0 0 4px 0', ...textSafe }}>
                    {edu.schoolName || edu.school || edu.institution || edu.universityName} | {startYear} - {endYear}
                  </p>
                  {edu.additionalCoursework && (
                    <p style={{ fontSize: fontSize, color: quaternaryColor, margin: '4px 0 0 0', ...textSafe }}>
                      {edu.additionalCoursework}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : null;

      case 'skills':
        return allSkills && allSkills.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h3 style={hStyle}>EXPERTISE</h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              fontSize: fontSize,
              lineHeight: `${lineSpacing / 20}`,
              color: secondaryColor,
              ...textSafe,
            }}>
              {allSkills.map((skill, i) => (
                <li key={i} style={{ marginBottom: `${paragraphSpacing / 10}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                  • {typeof skill === 'string' 
                    ? skill 
                    : skill.name 
                      ? `${skill.name}${skill.rating ? ` (${skill.rating}%)` : ''}` 
                      : (skill.skillName || skill.skill)
                  }
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'softwareSkills':
        return softwareSkills && softwareSkills.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h3 style={hStyle}>SOFTWARE SKILLS</h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              fontSize: fontSize,
              lineHeight: `${lineSpacing / 20}`,
              color: secondaryColor,
              ...textSafe,
            }}>
              {softwareSkills.map((skill, i) => (
                <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                  • {skill.name} — {skill.level}%
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'languages':
        return savedLanguages && savedLanguages.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h3 style={hStyle}>LANGUAGES</h3>
            <div style={{ fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, color: secondaryColor, ...textSafe }}>
              {savedLanguages.map((lang, i) => (
                <p key={i} style={{ marginBottom: `${paragraphSpacing / 10}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                  {typeof lang === 'string' ? lang : `${lang.name}${lang.level ? ` - ${lang.level}` : ''}`}
                </p>
              ))}
            </div>
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications && savedCertifications.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h3 style={hStyle}>CERTIFICATIONS</h3>
            <div style={{ fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, color: secondaryColor, ...textSafe }}>
              {savedCertifications.map((cert, i) => (
                <p key={i} style={{ marginBottom: `${paragraphSpacing / 2}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                  <strong style={{ color: mainTitleColor }}>{cert.year && `${cert.year} | `}{cert.provider}</strong><br/>
                  {cert.name}
                </p>
              ))}
            </div>
          </div>
        ) : null;

      case 'summary':
        return (profileSummary || savedSummary) ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>PROFILE</h2>
            <p style={{
              fontSize: fontSize,
              lineHeight: `${lineSpacing / 20}`,
              color: secondaryColor,
              whiteSpace: 'pre-line',
              paddingLeft: `${paragraphIndent}px`,
              ...textSafe,
            }}>
              {profileSummary || savedSummary}
            </p>
          </div>
        ) : null;

      case 'experience':
        return workHistory && workHistory.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>EXPERIENCES</h2>
            {workHistory.map((job, i) => {
              const startDate = job.startMonth && job.startYear 
                ? formatDate(job.startMonth, job.startYear)
                : (job.startYear || job.startDate || "");
              const endDate = job.current || job.currentlyWorking
                ? "Present" 
                : (job.endMonth && job.endYear 
                  ? formatDate(job.endMonth, job.endYear)
                  : (job.endYear || job.endDate || "Present"));

              return (
                <div key={i} style={{ marginBottom: `${paragraphSpacing * 1.5}px`, ...textSafe }}>
                  <h3 style={{ fontSize: `calc(${fontSize} * 1.1)`, margin: '0 0 6px 0', color: mainTitleColor, ...textSafe }}>
                    {job.employer || job.company || job.companyName} | {startDate} - {endDate}
                  </h3>
                  <p style={{ fontSize: fontSize, color: secondaryColor, margin: '0 0 12px 0', ...textSafe }}>
                    {job.jobTitle || job.position || job.role}
                    {job.location && ` | ${job.location}`}
                  </p>
                  {job.description && (
                    <div style={{ 
                      fontSize: fontSize, 
                      lineHeight: `${lineSpacing / 20}`,
                      paddingLeft: `${paragraphIndent}px`,
                      color: secondaryColor,
                      ...textSafe,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: job.description
                        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                        .replace(/\*(.*?)\*/g, "<em>$1</em>")
                        .replace(/<u>(.*?)<\/u>/g, "<u>$1</u>")
                        .replace(/\n/g, "<br/>"),
                    }}
                    />
                  )}
                  {job.responsibilities && job.responsibilities.length > 0 && (
                    <ul style={{ 
                      paddingLeft: `${20 + paragraphIndent}px`, 
                      margin: '8px 0 0 0', 
                      fontSize: fontSize, 
                      lineHeight: `${lineSpacing / 20}`,
                      color: secondaryColor,
                      ...textSafe,
                    }}>
                      {job.responsibilities.map((duty, idx) => (
                        <li key={idx} style={{ marginBottom: `${paragraphSpacing / 20}px`, ...textSafe }}>
                          {duty}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering && savedVolunteering.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>VOLUNTEERING</h2>
            {savedVolunteering.map((vol, i) => (
              <div key={i} style={{ marginBottom: `${paragraphSpacing * 1.2}px`, ...textSafe }}>
                <h3 style={{ fontSize: `calc(${fontSize} * 1.1)`, margin: '0 0 6px 0', color: mainTitleColor, ...textSafe }}>
                  {vol.subtopic}
                </h3>
                <p style={{ fontSize: fontSize, color: secondaryColor, margin: '0 0 8px 0', fontStyle: 'italic', ...textSafe }}>
                  {vol.fromDate} - {vol.toDate}
                </p>
                {vol.content && (
                  <p style={{ 
                    fontSize: fontSize, 
                    whiteSpace: 'pre-line', 
                    paddingLeft: `${paragraphIndent}px`,
                    lineHeight: `${lineSpacing / 20}`,
                    color: secondaryColor,
                    ...textSafe,
                  }}>
                    {vol.content}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : null;

      case 'accomplishments':
        return allRewards && allRewards.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>REWARDS</h2>
            <div style={{ fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, color: secondaryColor, ...textSafe }}>
              {allRewards.map((reward, i) => (
                <p key={i} style={{ marginBottom: `${paragraphSpacing}px`, whiteSpace: 'pre-line', paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                  • {reward}
                </p>
              ))}
            </div>
          </div>
        ) : null;

      case 'references':
        return allReferences && allReferences.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>REFERENCE</h2>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '60px',
              fontSize: fontSize,
              lineHeight: `${lineSpacing / 20}`,
              color: secondaryColor,
              ...textSafe,
            }}>
              {allReferences.map((ref, i) => {
                if (typeof ref === 'string') {
                  return (
                    <div key={i} style={{ whiteSpace: 'pre-line', ...textSafe }}>
                      {ref}
                    </div>
                  );
                }
                return (
                  <div key={i} style={{ ...textSafe }}>
                    <strong style={{ color: mainTitleColor, ...textSafe }}>{ref.refName || ref.name || ref.referenceName}</strong><br/>
                    <span style={{ ...textSafe }}>{ref.company || ref.companyName} {(ref.position || ref.jobTitle) && `/ ${ref.position || ref.jobTitle}`}</span><br/>
                    {ref.phone && <span style={{ ...textSafe }}>Phone: {ref.phone}<br/></span>}
                    {ref.email && <span style={{ ...textSafe }}>Email: {ref.email}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;

      case 'interests':
        return savedInterests && savedInterests.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>INTERESTS</h2>
            <ul style={{ 
              paddingLeft: `${20 + paragraphIndent}px`, 
              margin: '0', 
              fontSize: fontSize, 
              lineHeight: `${lineSpacing / 20}`,
              color: secondaryColor,
              ...textSafe,
            }}>
              {savedInterests.map((interest, i) => (
                <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, ...textSafe }}>
                  {interest}
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'websites':
        return savedWebsites && savedWebsites.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>WEBSITES / PROFILES</h2>
            <ul style={{ 
              paddingLeft: `${20 + paragraphIndent}px`, 
              margin: '0', 
              fontSize: fontSize, 
              lineHeight: `${lineSpacing / 20}`,
              color: secondaryColor,
              ...textSafe,
            }}>
              {savedWebsites.map((site, i) => (
                <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, ...textSafe }}>
                  {site.url || site}
                </li>
              ))}
            </ul>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo && savedAdditionalInfo.length > 0 ? (
          <div className="r50-section" style={{ ...textSafe }}>
            <h2 style={hStyle}>ADDITIONAL INFORMATION</h2>
            {savedAdditionalInfo.map((info, i) => (
              <p key={i} style={{ 
                marginBottom: `${paragraphSpacing / 10}px`,
                fontSize: fontSize,
                paddingLeft: `${paragraphIndent}px`,
                lineHeight: `${lineSpacing / 20}`,
                color: secondaryColor,
                ...textSafe,
              }}>
                {info}
              </p>
            ))}
          </div>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName];
          return items && items.length > 0 ? (
            <div className="r50-section" style={{ ...textSafe }}>
              <h2 style={hStyle}>
                {customSectionName.toUpperCase()}
              </h2>
              <ul style={{ 
                paddingLeft: `${20 + paragraphIndent}px`, 
                margin: '0', 
                fontSize: fontSize, 
                lineHeight: `${lineSpacing / 20}`,
                color: secondaryColor,
                ...textSafe,
              }}>
                {items.map((item, i) => (
                  <li key={i} style={{ marginBottom: `${paragraphSpacing / 20}px`, whiteSpace: 'pre-line', ...textSafe }}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : null;
        }
        return null;
    }
  };

  const getPageSections = (pageIdx) => {
    if (totalPages === 1 || pageBreaks.length === 0) {
      return rightSectionOrder.filter(id => renderSectionContent(id) !== null);
    }

    const container = rightContentRef.current;
    if (!container) return rightSectionOrder.filter(id => renderSectionContent(id) !== null);

    const sections = container.querySelectorAll(".r50-section");
    const pageHeight = pagePx.height - topBottomMargin * 2;
    const headerHeight = 120;

    // Right side pagination (existing)
    let accumulated = 0;
    let currentIdx = 0;
    const pageChunks = [[]];
    const visibleSectionIds = rightSectionOrder.filter(id => renderSectionContent(id) !== null);

    sections.forEach((section, i) => {
      const h = section.getBoundingClientRect().height;
      const secId = visibleSectionIds[i];
      if (!secId) return;

      const currentLimit = pageHeight - (currentIdx === 0 ? headerHeight : 0);

      if (accumulated + h > currentLimit && pageChunks[currentIdx].length > 0) {
        currentIdx++;
        pageChunks.push([]);
        accumulated = 0;
      }
      pageChunks[currentIdx].push(secId);
      accumulated += h;
    });

    // Return right side sections for the requested page
    return pageChunks[pageIdx] || [];
  };

  // --------------------------------------------------------------------------
  // Left sidebar pagination helper – calculates which left‑section ids belong to
  // a given page based on the same height constraints used for the right side.
  // --------------------------------------------------------------------------
  const getLeftPageSections = (pageIdx) => {
    const container = rightContentRef.current;
    if (!container) return leftSectionOrder.filter(id => renderSectionContent(id, true) !== null);

    const sections = container.querySelectorAll(".r50-section");
    const pageHeight = pagePx.height - topBottomMargin * 2;
    const headerHeight = 120;

    const leftVisibleIds = leftSectionOrder.filter(id => renderSectionContent(id, true) !== null);
    const leftChunks = [[]];
    let leftAccum = 0;
    let leftIdx = 0;
    sections.forEach((section, i) => {
      const secId = leftVisibleIds[i];
      if (!secId) return;
      const h = section.getBoundingClientRect().height;
      const currentLimit = pageHeight - (leftIdx === 0 ? headerHeight : 0);
      if (leftAccum + h > currentLimit && leftChunks[leftIdx].length > 0) {
        leftIdx++;
        leftChunks.push([]);
        leftAccum = 0;
      }
      leftChunks[leftIdx].push(secId);
      leftAccum += h;
    });
    return leftChunks[pageIdx] || [];
  };

  const getSectionsForCurrentPage = () => {
    return getPageSections(currentPage);
  };

  const getLeftSectionsForCurrentPage = () => {
    // Use the getLeftPageSections defined inside getPageSections scope
    // Since getLeftPageSections is defined there, we need to expose it via a closure.
    // We'll call the inner function via a temporary reference.
    // Create a temporary variable referencing the inner function.
    // This requires moving getLeftPageSections to component scope.
  };

  // Calculate totalPages and pageBreaks
  useEffect(() => {
    if (exporting) return;
    const container = rightContentRef.current;
    if (!container) return;

    const id = setTimeout(() => {
      const sections = Array.from(container.querySelectorAll(".r50-section"));
      const pageHeight = pagePx.height - (topBottomMargin) * 2;
      const headerHeight = 120;

      const breaks = [];
      let accumulated = 0;
      let pageIndex = 1;

      sections.forEach((section) => {
        const h = section.getBoundingClientRect().height;
        const currentLimit = pageHeight * pageIndex - (pageIndex === 1 ? headerHeight : 0);
        
        if (accumulated + h > currentLimit) {
          breaks.push(pageIndex);
          pageIndex++;
        }
        accumulated += h;
      });

      const total = Math.max(1, pageIndex);
      setTotalPages(total);
      setPageBreaks(breaks);
    }, 400);

    return () => clearTimeout(id);
  }, [
    rightSectionOrder, savedSummary, workHistory, educationEntries, education,
    savedSkills, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, references, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, savedAdditionalInfo, customSections,
    topBottomMargin, pagePx.height, exporting,
  ]);

  // Clamp currentPage if totalPages shrinks
  useEffect(() => {
    if (currentPage > totalPages - 1) {
      setCurrentPage(Math.max(0, totalPages - 1));
    }
  }, [totalPages, currentPage]);

  if (loading) {
    return (
      <div style={{
        width: page.width,
        minHeight: page.height,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: font,
        fontSize: fontSize,
      }}>
        Loading Resume...
      </div>
    );
  }

  const renderLeftSidebar = (pageIdx) => (
    <div
      style={{
        width: '36%',
        minWidth: 0,
        flexShrink: 0,
        backgroundColor: color,
        color: '#e0e0e0',
        padding: `${topBottomMargin}px ${sideMargins}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: `${sectionSpacing}px`,
        overflow: 'visible',
        boxSizing: 'border-box',
      }}
    >
      {/* Circular Photo only on Page 1 */}
      {pageIdx === 0 && photo && (
        <div style={{ alignSelf: 'center', marginBottom: `${paragraphSpacing}px` }}>
          <img
            src={photo}
            alt={`${firstName} ${lastName}`}
            style={{
              width: '170px',
              height: '170px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: `${lineWeight * 3}px solid #3a4a6b`,
              boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
              display: 'block',
            }}
          />
        </div>
      )}

      {/* CONTACT */}
      {pageIdx === 0 && (formData.phone || formData.email || formData.city || formData.linkedIn) && (
        <div style={{ ...textSafe }}>
          <h3 style={{
            fontSize: headingSize,
            fontWeight: 'bold',
            marginBottom: `${paragraphSpacing / 2}px`,
            color: '#ffffff',
            textTransform: 'uppercase',
            letterSpacing: `${1.5 * lineWeight}px`,
            ...textSafe,
          }}>
            CONTACT
          </h3>
          <div style={{ fontSize: fontSize, lineHeight: `${lineSpacing / 20}`, color: '#d0d0d0', ...textSafe }}>
            {formData.phone && (
              <p style={{ marginBottom: `${paragraphSpacing / 10}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                ☎ {formData.phone}
              </p>
            )}
            {formData.email && (
              <p style={{ marginBottom: `${paragraphSpacing / 10}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                ✉ {formData.email}
              </p>
            )}
            {formData.city && (
              <p style={{ marginBottom: `${paragraphSpacing / 10}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                📍 {formData.city}{formData.country ? `, ${formData.country}` : ''}
              </p>
            )}
            {formData.linkedIn && (
              <p style={{ marginBottom: `${paragraphSpacing / 10}px`, paddingLeft: `${paragraphIndent}px`, ...textSafe }}>
                🔗 <a 
                  href={formData.linkedIn.startsWith('http') ? formData.linkedIn : `https://${formData.linkedIn}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#68b0ff', textDecoration: 'none', ...textSafe }}
                >
                  {formData.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
                </a>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Render Left Column Sections for the current page only */}
      {getLeftPageSections(pageIdx).map(sectionId => (
        <React.Fragment key={sectionId}>
          {renderSectionContent(sectionId, true)}
        </React.Fragment>
      ))}
    </div>
  );

  const renderRightHeader = () => (
    <div style={{ ...textSafe }}>
      <h1 style={{
        fontSize: `calc(${headingSize} * 1.5)`,
        fontWeight: 'bold',
        margin: '0 0 8px 0',
        color: color,
        lineHeight: '1.05',
        ...textSafe,
      }}>
        {firstName}<br/>{lastName}
      </h1>
      <h2 style={{
        fontSize: `calc(${fontSize} * 1.3)`,
        fontWeight: '600',
        margin: '0',
        color: '#4a5b7a',
        ...textSafe,
      }}>
        {position}
      </h2>
    </div>
  );

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            size="small"
            color="primary"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">Page {currentPage + 1} / {totalPages}</Typography>
          <IconButton
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            size="small"
            color="primary"
          >
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <>
              <Button onClick={() => setArrangeDialogOpen(true)} variant="contained" startIcon={<Reorder />}>
                Arrange Sections
              </Button>
              <Button onClick={resetAll} variant="outlined" color="secondary">
                Reset All
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Hidden measurement container */}
      <Box
        ref={rightContentRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: `${pagePx.width * 0.64 - sideMargins * 2}px`,
          top: 0,
          left: -9999,
          zIndex: -1,
        }}
      >
        {rightSectionOrder.map((sectionId) => {
          const content = renderSectionContent(sectionId);
          return content ? <React.Fragment key={sectionId}>{content}</React.Fragment> : null;
        })}
      </Box>

      {/* Pages Wrapper */}
      <div id="resume-container">
        {exporting ? (
          Array.from({ length: totalPages }).map((_, pageIdx) => (
            <div
              key={pageIdx}
              className="resume-page"
              style={{
                width: page.width,
                height: page.height,
                backgroundColor: '#ffffff',
                color: '#333333',
                fontFamily: font,
                fontSize: fontSize,
                fontStyle: fontStyle,
                lineHeight: `${lineSpacing / 20}`,
                display: 'flex',
                margin: '0 auto 24px auto',
                overflow: 'hidden',
                boxSizing: 'border-box',
                pageBreakAfter: 'always',
              }}
            >
              {renderLeftSidebar(pageIdx)}

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: `${topBottomMargin}px ${sideMargins}px`,
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `${sectionSpacing}px`,
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}
              >
                {pageIdx === 0 && renderRightHeader()}

                {getPageSections(pageIdx).map(sectionId => {
                  return <React.Fragment key={sectionId}>{renderSectionContent(sectionId, false)}</React.Fragment>;
                })}
              </div>
            </div>
          ))
        ) : (
          <div
            className="resume-page"
            style={{
              width: page.width,
              height: page.height,
              minHeight: page.height,
              backgroundColor: '#ffffff',
              color: '#333333',
              fontFamily: font,
              fontSize: fontSize,
              fontStyle: fontStyle,
              lineHeight: `${lineSpacing / 20}`,
              display: 'flex',
              margin: '0 auto',
              boxShadow: '0 4px 25px rgba(0,0,0,0.1)',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            {renderLeftSidebar(currentPage)}

            <div
              style={{
                flex: 1,
                minWidth: 0,
                padding: `${topBottomMargin}px ${sideMargins}px`,
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                gap: `${sectionSpacing}px`,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              {currentPage === 0 && renderRightHeader()}

              {getSectionsForCurrentPage().map(sectionId => {
                return <React.Fragment key={sectionId}>{renderSectionContent(sectionId, false)}</React.Fragment>;
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Arrange Sections Dialog ── */}
      <Dialog open={arrangeDialogOpen} onClose={() => setArrangeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Reorder /> Arrange Resume Sections
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Drag to reorder sections or move them between sidebar and main content area.
          </Typography>
          <Box style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {renderDragPanel("left",  leftSectionOrder)}
            {renderDragPanel("right", rightSectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAll} color="secondary">Reset All</Button>
          <Button variant="contained" onClick={() => setArrangeDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Resume50;